import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import * as _ from 'lodash';
import * as moment from 'moment';
import CopyToClipboard from '../../../generic/CopyToClipboard';
import { ErrorAlert } from '../../../generic';
import Text from '../../../generic/Text';
import type { NewPortalToken } from '../../../../../shared/publicPortal/types';
import css from './ModalCreatePortalToken.module.less';
import Flex from '../../../generic/Flex';
import MutatePortalTokenModal from './components/MutatePortalTokenModal';
import type { ReleaseGroupOption } from './types';
import { expirationOptions } from './constants';

export type ModalMode = 'Create' | 'Hide';

type ModalCreatePortalTokenProps = {
  mode: ModalMode;
  isCreating: boolean;
  errors: string[] | null;
  onHide: () => void;
  onCreateToken: (token: NewPortalToken) => Promise<string | null>;
};

const ModalCreatePortalToken: VoidFunctionComponent<ModalCreatePortalTokenProps> = ({
  mode,
  isCreating,
  errors,
  onHide,
  onCreateToken,
}) => {
  const shouldShowModal = mode !== 'Hide';
  const [newName, setNewName] = useState<string>('');
  const [selectedExpirationOption, setSelectedExpirationOption] = useState(expirationOptions[0]);
  const [selectedReleaseGroups, setSelectedReleaseGroups] = useState<ReleaseGroupOption[]>([]);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  let expiration: Date | null = null;
  if (selectedExpirationOption?.value) {
    const { numerator, unit } = selectedExpirationOption.value;
    expiration = moment().add(numerator, unit).toDate();
  }

  const onCreateNewToken = async () => {
    const tokenValue = await onCreateToken({
      name: newName,
      expiration,
      releaseGroups: selectedReleaseGroups.map(({ value }) => {
        const id = _.toNumber(value);
        return { id };
      }),
    });
    setCreatedToken(tokenValue);
  };

  const onHideModal = () => {
    onHide();
    setNewName('');
    setSelectedExpirationOption(expirationOptions[0]);
    setSelectedReleaseGroups([]);
    setCreatedToken(null);
  };

  if (!shouldShowModal) {
    // Prevent the modal animation on close. Without this, the new token modal will revert back to the create token modal causing a flicker.
    return null;
  }

  // Show the token after it has been created
  if (createdToken) {
    return (
      <Modal show={shouldShowModal} onHide={onHideModal}>
        <Modal.Header className={css.header} closeButton>
          <h1 className={css.heading}>Token created</h1>
        </Modal.Header>
        <Modal.Body className={css.body}>
          {!_.isEmpty(errors) && <ErrorAlert status={{ errors }} />}
          <div className="margin-bottom-lg">
            <div className={css.headingDescription}>Copy this and share it with the user. </div>
            <div className={css.copyToken}>
              <CopyToClipboard text={createdToken}>
                <div className={css.copyTokenDisplay}>
                  <Flex align="flex-start" justify="stretch" direction="column">
                    <Text.H5>Token</Text.H5>
                    <div className={css.token}>{createdToken}</div>
                  </Flex>
                </div>
              </CopyToClipboard>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // Show the initial create token modal
  return (
    <MutatePortalTokenModal
      shouldShowModal={shouldShowModal}
      errors={errors || []}
      name={newName}
      setName={setNewName}
      expiration={expiration}
      expirationOption={selectedExpirationOption}
      setExpirationOption={setSelectedExpirationOption}
      selectedReleaseGroups={selectedReleaseGroups}
      setSelectedReleaseGroups={setSelectedReleaseGroups}
      onHide={onHideModal}
      onMutateToken={onCreateNewToken}
      type="Create"
      isSaving={isCreating}
    />
  );
};

export default ModalCreatePortalToken;
